import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { EducationalPathUserDTO } from '../../../dto/educational-path/educational-path-user.dto';
import { ParticipationEventDTO } from '../../../dto/event/participation-event.dto';
import { DunaValidationStatus } from '../../../dto/users/user-status.dto';
import { DunaValidationBadgeComponent } from '../duna-validation-badge/duna-validation-badge.component';

export interface ProfilePathModuleSummary {
  id: string;
  moduleId?: number | null;
  name: string;
  completed: boolean;
  status: 'completed' | 'in_progress' | 'not_started';
  minutes?: number;
  maintenance_minutes?: number;
  completed_via_manual?: boolean;
}

export interface ProfileParticipationEventModuleDisplay {
  name: string;
  minutes: number;
}

export interface DunaProfilePathDetailHost {
  showMaintenanceManualExtras(path: EducationalPathUserDTO): boolean;
  getProfileYear(): number;
  getMaintenanceManualMinutes(path: EducationalPathUserDTO): number;
  getMaintenanceManualAcquireAll(path: EducationalPathUserDTO): boolean;
  pathRequiresMaintenance(path: EducationalPathUserDTO): boolean;
  isPathWithNoMaintenanceAndNoRequiredModules(path: EducationalPathUserDTO): boolean;
  getProfileStatusScopeLabel(path: EducationalPathUserDTO): string;
  hasMeritForCurrentPeriod(path: EducationalPathUserDTO): boolean;
  getCurrentYearStatus(path: EducationalPathUserDTO): string;
  getCurrentYearStatusText(status: string, path: EducationalPathUserDTO): string;
  getDunaStatusPeriodLabel(path: EducationalPathUserDTO): string;
  getDunaValidationStatus(path: EducationalPathUserDTO): DunaValidationStatus;
  getDunaValidationTooltip(status: string, path: EducationalPathUserDTO): string;
  getMaintenanceBarTooltip(path: EducationalPathUserDTO): string;
  getMaintenanceProgressPercentage(path: EducationalPathUserDTO): number;
  getMaintenanceRegisteredBarExtraPercentage(
    path: EducationalPathUserDTO
  ): number;
  getMaintenanceCompletedMinutesDisplayed(path: EducationalPathUserDTO): number;
  getRequiredModules(path: EducationalPathUserDTO): ProfilePathModuleSummary[];
  getOptionalModules(path: EducationalPathUserDTO): ProfilePathModuleSummary[];
  getPathParticipationEvents(path: EducationalPathUserDTO): ParticipationEventDTO[];
  getParticipationEventPathModules(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): ProfileParticipationEventModuleDisplay[];
  getParticipationEventCreditTooltip(
    path: EducationalPathUserDTO,
    event: ParticipationEventDTO
  ): string | null;
  showModuleEventDate(module: ProfilePathModuleSummary): boolean;
  getModuleEventDate(module: ProfilePathModuleSummary): string | null;
  getModuleEventDateLabel(module: ProfilePathModuleSummary): string;
  getModuleEventDateTooltip(module: ProfilePathModuleSummary): string;
  getModuleMaintenanceMinutes(module: ProfilePathModuleSummary): number;
  getModuleMaintenanceCreditText(module: ProfilePathModuleSummary): string;
  getModuleDescription(module: ProfilePathModuleSummary): string;
  getModuleCalendarModuleId(
    path: EducationalPathUserDTO,
    module: ProfilePathModuleSummary
  ): number | null;
  hasModuleDetailPanel(
    path: EducationalPathUserDTO,
    module: ProfilePathModuleSummary
  ): boolean;
}

export interface DunaProfilePathDetailDialogData {
  path: EducationalPathUserDTO;
  pathIndex: number;
  host: DunaProfilePathDetailHost;
}

@Component({
  selector: 'app-duna-profile-path-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    DunaValidationBadgeComponent,
  ],
  templateUrl: './duna-profile-path-detail-dialog.component.html',
  styleUrl: './duna-profile-path-detail-dialog.component.scss',
})
export class DunaProfilePathDetailDialogComponent {
  /** Expanded module cards (description + calendar link). */
  expandedModuleIds: Record<string, boolean> = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DunaProfilePathDetailDialogData,
    private dialogRef: MatDialogRef<DunaProfilePathDetailDialogComponent>
  ) {}

  get host(): DunaProfilePathDetailDialogData['host'] {
    return this.data.host;
  }

  get path(): EducationalPathUserDTO {
    return this.data.path;
  }

  close(): void {
    this.dialogRef.close();
  }

  toggleModuleExpand(moduleId: string, event: Event): void {
    event.stopPropagation();
    this.expandedModuleIds[moduleId] = !this.expandedModuleIds[moduleId];
  }

  isModuleExpanded(moduleId: string): boolean {
    return !!this.expandedModuleIds[moduleId];
  }

  moduleCalendarQueryParams(moduleId: number): { mods: string } {
    return { mods: String(moduleId) };
  }

  moduleStatusLabel(module: ProfilePathModuleSummary): string {
    switch (module.status) {
      case 'completed':
        return module.completed_via_manual ? 'Opfyldt (manuel)' : 'Deltaget';
      case 'in_progress':
        return 'Tilmeldt';
      default:
        return 'Ikke tilmeldt';
    }
  }

  moduleStatusIcon(module: ProfilePathModuleSummary): string {
    switch (module.status) {
      case 'completed':
        return module.completed_via_manual ? 'verified' : 'check_circle';
      case 'in_progress':
        return 'event_available';
      default:
        return 'radio_button_unchecked';
    }
  }

  moduleCardClasses(module: ProfilePathModuleSummary): Record<string, boolean> {
    return {
      'module-card--completed': module.status === 'completed',
      'module-card--in-progress': module.status === 'in_progress',
      'module-card--not-started': module.status === 'not_started',
      'module-card--manual': Boolean(module.completed_via_manual),
    };
  }

  eventStatusLabel(event: ParticipationEventDTO): string {
    const status = (event.status ?? '').toLowerCase();
    if (status === 'completed' || status === 'shownup') {
      return 'Deltaget';
    }
    if (
      status === 'pending' ||
      status === 'waitlist' ||
      status === 'in_progress'
    ) {
      return 'Tilmeldt';
    }
    return 'Event';
  }

  eventStatusClass(event: ParticipationEventDTO): string {
    const status = (event.status ?? '').toLowerCase();
    if (status === 'completed' || status === 'shownup') {
      return 'event-card__chip--completed';
    }
    if (
      status === 'pending' ||
      status === 'waitlist' ||
      status === 'in_progress'
    ) {
      return 'event-card__chip--enrolled';
    }
    return 'event-card__chip--neutral';
  }
}
